import { useEffect, useRef, useState } from 'react'
import { isDisplayableImageUrl } from '../utils/itemMeta'
import './ProductImageLightbox.css'

function normalizeSlides(slides, src) {
  const incoming = Array.isArray(slides) && slides.length
    ? slides
    : (src ? [{ src, label: 'Image du produit' }] : [])
  const seen = new Set()
  return incoming.filter((slide) => {
    const url = String(slide?.src || slide || '').trim()
    if (!isDisplayableImageUrl(url) || seen.has(url)) return false
    seen.add(url)
    return true
  }).map((slide) => (
    typeof slide === 'string'
      ? { src: slide, label: 'Image du produit' }
      : { src: slide.src, label: slide.label || 'Image du produit' }
  ))
}

export default function ProductImageLightbox({ src, slides, onClose }) {
  const items = normalizeSlides(slides, src)
  const trackRef = useRef(null)
  const indexRef = useRef(0)
  const [index, setIndex] = useState(0)
  const [zoomed, setZoomed] = useState(false)

  useEffect(() => {
    indexRef.current = index
  }, [index])

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose?.()
        return
      }
      const current = indexRef.current
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        trackRef.current?.children[Math.min(items.length - 1, current + 1)]?.scrollIntoView({ behavior: 'smooth', inline: 'start' })
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        trackRef.current?.children[Math.max(0, current - 1)]?.scrollIntoView({ behavior: 'smooth', inline: 'start' })
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [items.length, onClose])

  if (!items.length) return null

  return (
    <div className="product-image-lightbox" onClick={onClose} role="dialog" aria-modal="true" aria-label="Galerie d’images du produit">
      <div className="product-image-lightbox-frame" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="product-image-lightbox-close" onClick={onClose} aria-label="Fermer la galerie">
          ×
        </button>
        <div
          ref={trackRef}
          className="product-image-lightbox-track"
          onScroll={(event) => {
            const next = Math.round(event.currentTarget.scrollLeft / Math.max(event.currentTarget.clientWidth, 1))
            if (next !== index) {
              setIndex(next)
              setZoomed(false)
            }
          }}
        >
          {items.map((slide, slideIndex) => (
            <figure
              key={slide.src}
              className={`product-image-lightbox-slide${zoomed && slideIndex === index ? ' is-zoomed' : ''}`}
            >
              <img
                src={slide.src}
                alt={slide.label}
                onDoubleClick={() => setZoomed((value) => !value)}
              />
              <figcaption className="product-image-lightbox-label">{slide.label}</figcaption>
            </figure>
          ))}
        </div>
        {items.length > 1 ? (
          <div className="product-image-lightbox-dots" aria-hidden="true">
            {items.map((slide, slideIndex) => (
              <span key={slide.src} className={slideIndex === index ? 'is-active' : ''} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}
